import React, { Key, useEffect, useState } from 'react';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { SelectableListDataType, SelectableList } from '../../../../../shared/selectable-list/selectable-list';
import {
  StaffActiveCaseListContainerCredentialVerificationCasesV1ByApplicantDocument,
  StaffActiveCaseListContainerIdentityVerificationCasesV1ByApplicantDocument,
  StaffActiveCaseListContainerSendReportCasesV1ByApplicantDocument,
  SharedArchivedCaseListContainerArchivedCasesByApplicantIdDocument
} from '../../../../../../generated'
import { useQuery } from '@apollo/client';
import { useNavigate, useResolvedPath } from 'react-router-dom';
import { Empty } from 'antd';
import { DATE_TIME_FORMAT } from '../../../../../../constants';
import { FormatDate } from '../../../../../../constants';
interface RelatedCaseListCommonContainerProps {
  refetch?: boolean;
  applicantId: string;
  caseId: string;
  setDisabled: (disabled: boolean) => void;
}

const formatCamelCase = (camelCaseString: string) => {
  return camelCaseString
    .replace(/caseWorker/g, 'Caseworker')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (str) => str.toUpperCase());
};

export const RelatedCaseListCommonContainer: React.FC<RelatedCaseListCommonContainerProps> = ({ refetch, applicantId, caseId, setDisabled }) => {
  const [selectedCase, setSelectedCase] = useState<SelectableListDataType | undefined>();
  const navigate = useNavigate();

  const {
    data: identityVerificationCasesV1Data,
    loading: identityVerificationCasesV1Loading,
    error: identityVerificationCasesV1Error,
    refetch: refetchIdentityVerification
  } = useQuery(StaffActiveCaseListContainerIdentityVerificationCasesV1ByApplicantDocument, {
    variables: { applicantId },
    skip: !applicantId
  });

  const {
    data: credentialVerificationCasesV1Data,
    loading: credentialVerificationCasesV1Loading,
    error: credentialVerificationCasesV1Error,
    refetch: refetchCredentialVerification
  } = useQuery(StaffActiveCaseListContainerCredentialVerificationCasesV1ByApplicantDocument, {
    variables: { applicantId },
    skip: !applicantId
  });

  const {
    data: sendReportCasesV1Data,
    loading: sendReportCasesV1Loading,
    error: sendReportCasesV1Error,
    refetch: refetchSendReport
  } = useQuery(StaffActiveCaseListContainerSendReportCasesV1ByApplicantDocument, {
    variables: { applicantId },
    skip: !applicantId
  });

  const {
    data: archiveCaseData,
    loading: archiveCaseLoading,
    error: archiveCaseError,
    refetch: refetchArchive
  } = useQuery(SharedArchivedCaseListContainerArchivedCasesByApplicantIdDocument, {
    variables: { id: applicantId },
    skip: !applicantId,
    context: {
      headers: {
        'cache-enabled': 'true'
      }
    },
  });

  useEffect(() => {
    if (refetch) {
      refetchIdentityVerification();
      refetchCredentialVerification();
      refetchSendReport();
      refetchArchive();
    }
  }, [refetch, refetchIdentityVerification, refetchCredentialVerification, refetchSendReport, refetchArchive]);

  useEffect(() => {
    if (identityVerificationCasesV1Data?.identityVerificationCasesV1ByApplicant) {
      const allRejected = identityVerificationCasesV1Data.identityVerificationCasesV1ByApplicant.every(
        (item) => item?.caseDetails?.applicationReview?.decision?.result === 'rejected'
      );
      setDisabled(!allRejected);
    }
  }, [identityVerificationCasesV1Data, setDisabled]);

  const formattedData: SelectableListDataType[] = [
    ...(identityVerificationCasesV1Data?.identityVerificationCasesV1ByApplicant?.map((item) => ({
      key: item?.id,
      caseType: item?.caseType,
      title: item?.caseName || '',
      initials: item?.caseName?.substring(0, 2).toUpperCase() || '',
      progress: formatCamelCase(item?.state ?? ''),
      state: item?.state || '',
      schemaVersion: '',
      timestamp: FormatDate(item?.lastActivityAt, DATE_TIME_FORMAT),
      isArchived: false
    })) || []),
    ...(credentialVerificationCasesV1Data?.credentialVerificationCasesV1ByApplicant?.map((item) => ({
      key: item?.id,
      caseType: item?.caseType,
      title: item?.caseName || '',
      initials: item?.caseName?.substring(0, 2).toUpperCase() || '',
      progress: formatCamelCase(item?.state ?? ''),
      state: item?.state || '',
      schemaVersion: '',
      timestamp: FormatDate(item?.lastActivityAt, DATE_TIME_FORMAT),
      isArchived: false
    })) || []),
    ...(sendReportCasesV1Data?.sendReportCasesV1ByApplicant?.map((item) => ({
      key: item?.id,
      caseType: item?.caseType,
      title: item?.caseName || '',
      initials: item?.caseName?.substring(0, 2).toUpperCase() || '',
      progress: formatCamelCase(item?.state ?? ''),
      state: item?.state || '',
      schemaVersion: '',
      timestamp: FormatDate(item?.lastActivityAt, DATE_TIME_FORMAT),
      isArchived: false
    })) || []),
    ...(archiveCaseData?.archivedCasesByApplicantId?.map((item) => ({
      key: item?.id,
      caseType: item?.caseType,
      title: item?.caseName || '',
      initials: item?.caseName?.substring(0, 2).toUpperCase() || '',
      progress: formatCamelCase(item?.state ?? ''),
      state: item?.state || '',
      schemaVersion: '',
      timestamp: FormatDate(item?.archivedAt, DATE_TIME_FORMAT),
      isArchived: true
    })) || [])
  ];

  const currentRoutePath = useResolvedPath('').pathname;
  const pathSegments = currentRoutePath.split('/');
  const casesIndex = pathSegments.indexOf('cases');
  const newLocation = pathSegments.slice(0, casesIndex + 1).join('/');
  const getNavigationPath = (id: string, caseType: string) => {
    const selectedCase = formattedData.find((item) => item.key === id);
    if (selectedCase?.isArchived) {
      return `${newLocation}/${id}/case-type/${caseType}/archived`;
    }
    return `${newLocation}/${id}/case-type/${caseType}/active/v1/chat`;
  };

  const onCaseSelected = (selectedRowKey: Key, caseType: string) => {
    const selectedCase = formattedData.find((r) => r.key === selectedRowKey);
    setSelectedCase(selectedCase);
    const newUrl = getNavigationPath(selectedRowKey.toString(), caseType);
    navigate(newUrl, { replace: true });
  };

  return (
    <ComponentQueryLoader
      loading={identityVerificationCasesV1Loading || credentialVerificationCasesV1Loading || sendReportCasesV1Loading || archiveCaseLoading}
      error={identityVerificationCasesV1Error || credentialVerificationCasesV1Error || sendReportCasesV1Error || archiveCaseError}
      hasData={formattedData && formattedData.filter((item) => item.key !== caseId).length > 0}
      hasDataComponent={<SelectableList data={formattedData.filter((item) => item.key !== caseId)} selectedRecord={selectedCase} onSelectChange={onCaseSelected} />}
      noDataComponent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    />
  );
};
